import { ElAvatar, ElButton, ElIcon, ElMessageBox, ElScrollbar, ScrollbarRef } from '@qsxy/element-plus-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './chat-container.scss';
import ChatInput from './ChatInput';
import MessageBubble from './MessageBubble';
import { useAppContext } from '@/AppContext';
import { RootState } from '@/typings';
import { useSelector } from 'react-redux';

const ChatContainer = forwardRef((props: Props, ref) => {
	const loginUser = useSelector<RootState, RootState['user']>(state => state.user ?? {});
	const { title, loading, messages, send, onRename, suggestions = [] } = props;
	const messagesContainer = useRef<HTMLDivElement>(null);
	const [isManualScroll, setIsManualScroll] = useState(false);
	const scrollRef = useRef<ScrollbarRef>(null);
	// const oldMessages = useRef([]);
	const { conversationTitle } = useAppContext();

	const [initialLoad, setInitialLoad] = useState(true); // 添加初始加载标记

	const handleEditTitle = () => {
		ElMessageBox.prompt('请输入标题', '修改标题', {
			inputValue: conversationTitle.current,
			inputErrorMessage: '请输入标题',
			inputValidator: value => !!value,
		}).then(({ value }) => {
			onRename?.(value);
		});
	};

	// 滚动到底部方法
	const scrollToBottomCore = useCallback(() => {
		scrollRef.current.setScrollTop(scrollRef.current.wrapRef.current.scrollHeight);
	}, []);

	// 滚动到底部方法
	const scrollToBottom = useCallback(async () => {
		await requestAnimationFrame;
		if (scrollRef.current && !isManualScroll) {
			scrollToBottomCore();
		}
	}, [isManualScroll, scrollToBottomCore]);

	// 如果用户手动滚动，则不自动滚动到底部
	const handleScroll = () => {
		// console.log(Math.ceil(scrollRef.current.wrapRef.current.scrollTop + scrollRef.current.wrapRef.current.offsetHeight), scrollRef.current.wrapRef.current.scrollHeight);
		if (Math.ceil(scrollRef.current.wrapRef.current.scrollTop + scrollRef.current.wrapRef.current.offsetHeight) < scrollRef.current.wrapRef.current.scrollHeight) {
			setIsManualScroll(true);
		} else {
			setIsManualScroll(false);
		}
	};

	// 组件挂载时滚动到底部
	useEffect(() => {
		scrollToBottom();
		requestAnimationFrame(() => {
			setInitialLoad(true);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// 跳过初始加载的消息
		if (initialLoad) {
			setInitialLoad(false);
			return;
		}

		// if (loading) {
		// }
		scrollToBottom();
		// if (messages.length > oldMessages.current?.length) {
		// 	const lastMessage = messages[messages.length - 1];
		// 	// 只在非流式模式下启用打字机效果
		// 	if (lastMessage.role === 'assistant' && !props.streamMode) {
		// 		setIsTyping(true);
		// 	}
		// 	oldMessages.current = messages;
		// }

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, messages]);

	useEffect(() => {
		scrollToBottom();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [suggestions]);

	useEffect(() => {
		window.addEventListener('resize', handleScroll);
		return () => {
			window.removeEventListener('resize', handleScroll);
		};
	}, []);

	useImperativeHandle(ref, () => ({
		scrollToBottom: scrollToBottomCore,
	}));

	return (
		<div className="ttn-chat-container">
			<div className="chat-header" onClick={handleEditTitle}>
				<div className="header-left">
					<h3>{title}</h3>
				</div>
				{title?.length > 0 && !loginUser?.isVisitor && <ElIcon name="edit" prefix="fas" />}
			</div>
			{/* 消息列表区域：包含所有对话内容  */}
			<ElScrollbar ref={scrollRef} always width={10} showHorizontal={false} onScroll={handleScroll}>
				<div className="chat-messages" ref={messagesContainer}>
					{/* 循环渲染消息气泡 */}
					{messages.map((item, index) => (
						<MessageBubble key={index} content={item.content} isUser={item.role === 'user'} id={item?.conversation?.messageId} thinkingTime={item.thinkingTime} />
					))}

					{/* AI思考中状态显示 */}
					{loading && (!messages.length || messages[messages.length - 1].role === 'user') && (
						<div className="message message-ai thinking-message">
							<div className="message-content">
								<div className="avatar-wrapper">
									<ElAvatar size={40} className="ai-avatar">
										<ElIcon name="headphones" />
									</ElAvatar>
								</div>
								<div className="bubble-wrapper">
									<div className="bubble thinking-bubble">
										<div className="dots-container">
											<span className="dot"></span>
											<span className="dot"></span>
											<span className="dot"></span>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{loading || suggestions.length === 0 ? null : (
						<div className="ttn-suggestions">
							<div>你可以继续问我 :</div>
							<div className="links">
								{suggestions.map((item, index) => (
									<ElButton key={index} plain type="primary" onClick={() => send(item)}>
										{item}
									</ElButton>
								))}
							</div>
						</div>
					)}

					{isManualScroll && (
						<div className="scroll-down-btn">
							<ElButton icon="angle-down" circle size="large" onClick={scrollToBottomCore}></ElButton>
						</div>
					)}
				</div>
			</ElScrollbar>
			{/* 输入区域组件 */}
			<ChatInput
				disabled={loading}
				send={(message: string) => {
					scrollToBottom();
					send(message);
					setIsManualScroll(false);
				}}
			/>
			{/* <div className="chat-content"></div> */}
		</div>
	);
});

interface Props {
	title?: string; // 聊天标题
	messages: Message[]; // 消息列表
	loading?: boolean; // 加载状态
	streamMode?: boolean; // 流式模式
	suggestions?: string[];
	send?: (message: string) => void;
	onRename?: (title: string) => Promise<any>;
}

// 消息类型定义
export interface Message {
	role: 'user' | 'assistant' | 'system';
	content: string;
	thinkingTime?: number;
	conversation?: {
		id?: string;
		messageId?: string;
	};
	/** 是否正在回答中 */
	loading?: boolean;
}

export default ChatContainer;
